import { resizeManager } from './resizeManager'

class HeaderSlideDown {
  private toggleButton: HTMLElement | null = null
  private content: HTMLElement | null = null
  private chevronIcon: HTMLElement | null = null
  private isOpen: boolean = false
  private unsubscribe: (() => void) | null = null

  constructor() {
    this.init()
  }

  private init(): void {
    this.toggleButton = document.getElementById('toggle-slide-down-header')
    this.content = document.getElementById('content-above-header')
    this.chevronIcon = document.getElementById('content-above-chevron-icon')

    if (!this.toggleButton || !this.content) {
      return
    }

    this.setupEventListeners()

    this.unsubscribe = resizeManager.subscribe('header-slide-down', (isSmall) => {
      if (isSmall && this.isOpen) {this.close()}
    })
  }

  private setupEventListeners(): void {
    if (!this.toggleButton) {return}

    this.toggleButton.addEventListener('click', () => this.toggle())
    this.toggleButton.addEventListener('keydown', (e: KeyboardEvent) => this.onKeyDown(e))
    document.addEventListener('click', (e: MouseEvent) => this.handleOutsideClick(e))
  }

  private toggle(): void {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  private open(): void {
    if (!this.content || !this.toggleButton) {
      return
    }

    this.isOpen = true
    this.content.style.display = 'block'
    this.toggleButton.setAttribute('aria-expanded', 'true')
    this.toggleButton.setAttribute('aria-label', 'Stäng FAR:s webbplatser')

    if (this.chevronIcon) {
      this.chevronIcon.classList.add('-rotate-180')
    }

    this.enter(this.content)
  }

  private close(): void {
    if (!this.content || !this.toggleButton) {
      return
    }

    this.isOpen = false
    this.toggleButton.setAttribute('aria-expanded', 'false')
    this.toggleButton.setAttribute('aria-label', 'Öppna FAR:s webbplatser')

    if (this.chevronIcon) {
      this.chevronIcon.classList.remove('-rotate-180')
    }

    if (document.activeElement === this.toggleButton) {
      this.toggleButton.blur()
    }

    this.leave(this.content, () => {
      if (this.content) {
        this.content.style.display = 'none'
      }
    })
  }

  private handleOutsideClick(event: MouseEvent): void {
    if (!this.isOpen || !this.content || !this.toggleButton) {
      return
    }

    const target = event.target as Node
    const isClickInContent = this.content.contains(target)
    const isClickOnToggle = this.toggleButton.contains(target)

    if (!isClickInContent && !isClickOnToggle) {
      this.close()
    }
  }

  private enter(el: HTMLElement): void {
    el.style.maxHeight = '0px'
    el.style.opacity = '0'
    el.style.overflow = 'hidden'

    requestAnimationFrame(() => {
      el.style.transition = 'max-height 0.5s ease, opacity 0.7s ease'
      el.style.maxHeight = el.scrollHeight + 'px'
      el.style.opacity = '1'

      setTimeout(() => {
        el.style.maxHeight = 'none'
        el.style.transition = ''
        el.style.overflow = ''
      }, 500)
    })
  }

  private leave(el: HTMLElement, done: () => void): void {
    el.style.maxHeight = el.scrollHeight + 'px'
    el.style.opacity = '1'
    el.style.overflow = 'hidden'

    requestAnimationFrame(() => {
      el.style.transition = 'max-height 0.3s ease, opacity 0.2s ease'
      el.style.maxHeight = '0px'
      el.style.opacity = '0'

      setTimeout(() => {
        el.style.transition = ''
        el.style.overflow = ''
        done()
      }, 300)
    })
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isOpen) {
      this.close()
    }
  }

  public destroy(): void {
    document.removeEventListener('click', (e: MouseEvent) => this.handleOutsideClick(e))
    this.unsubscribe?.()
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new HeaderSlideDown()
  })
} else {
  new HeaderSlideDown()
}

export default HeaderSlideDown
